/**
 * Data layer for `os-breakdown-widget`. Thin typed fetch wrappers over /api/v1/os-breakdown-widget.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface OsBreakdownWidgetRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListOsBreakdownWidgetResponse {
	count: number;
	items: OsBreakdownWidgetRow[];
}

export interface CreateOsBreakdownWidgetBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/os-breakdown-widget";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListOsBreakdownWidgetResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListOsBreakdownWidgetResponse>(res);
}

export async function create(body: CreateOsBreakdownWidgetBody): Promise<OsBreakdownWidgetRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<OsBreakdownWidgetRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const osBreakdownWidgetQueryKeys = {
	all: ["os-breakdown-widget"] as const,
	list: () => [...osBreakdownWidgetQueryKeys.all] as const,
	detail: (id: string) => [...osBreakdownWidgetQueryKeys.all, id] as const,
};
